<template>
  <FriedFloater
    placement="bottom"
    :show="showTasksPopper"
    :main-axis-offset="0"
    :cross-axis-offset="16"
  >
    <template #reference>
      <div
        :class="[
          'milestone-description',
          { completed: milestoneCompleted, active: milestoneActive },
        ]"
        @mouseover="showTasksPopper = true"
        @mouseleave="showTasksPopper = false"
      >
        <FriedH5>
          {{ milestone.title }}
        </FriedH5>
        <FriedParagraph :size="ParagraphSize.Small">
          {{
            t('map_completed_tasks_tooltip', {
              totalNumberOfTasks: milestoneProgress.taskCount,
              completedNumberOfTasks: milestoneProgress.completedTaskCount,
            })
          }}
        </FriedParagraph>
        <FriedH5 v-if="isDeadlineToday && !milestoneCompleted" class="deadline">
          {{ milestoneDeadline }}
        </FriedH5>
        <FriedParagraph v-else :size="ParagraphSize.Small" class="deadline">
          {{ milestoneDeadline }}
        </FriedParagraph>
      </div>
    </template>
    <div v-if="milestone.tasks.length" class="tasks-popper">
      <div
        v-for="(task, index) in milestone.tasks"
        :key="task.id"
        :class="['task-title-container', { completed: isTaskCompleted(task) }]"
      >
        <div
          :class="[
            'counter',
            {
              today: isTaskDeadlineToday(task) && !isTaskCompleted(task),
            },
          ]"
        >
          <div v-if="isTaskCompleted(task)" class="icon-container">
            <FriedIcon :size="IconSize.Small" icon="bold-check-mark" />
          </div>
          <div v-else-if="isTaskDeadlineToday(task)" class="icon-container">
            <FriedIcon :size="IconSize.Small" icon="alarm-bell" />
          </div>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <FriedH5 v-if="isTaskCompleted(task)">{{ task.title }}</FriedH5>
        <FriedParagraph v-else :size="ParagraphSize.Small">
          {{ task.title }}
        </FriedParagraph>
      </div>
    </div>
  </FriedFloater>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { ref, computed, defineComponent } from 'vue';
import { ParagraphSize, IconSize } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import type { Milestone, Task } from '../types/action-plan';
import { TaskStatus } from '../types/enums/task-status';
import { MilestoneHelper } from '../helpers/milestone.helper';
import { TaskHelper } from '../helpers/task.helper';

export default defineComponent({
  props: {
    milestone: {
      type: Object as PropType<Milestone>,
      required: true,
    },
    isDeadlineToday: {
      type: Boolean,
    },
    farthestUncompletedTask: {
      type: Object as PropType<Task>,
      default: null,
    },
    milestoneCompleted: {
      type: Boolean,
    },
    milestoneActive: {
      type: Boolean,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const showTasksPopper = ref(false);
    const milestoneProgress = computed(() => MilestoneHelper.getMilestoneProgress(props.milestone));
    const milestoneDeadline = computed(() => {
      if (!props.farthestUncompletedTask) {
        return `${t('map_task_deadline')}: ${t('map_no_deadline')}`;
      }
      const deadline = TaskHelper.getDeadline(
        DateTime.fromMillis(props.farthestUncompletedTask.deadline)
      );

      return `${t('map_task_deadline')}: ${deadline}`;
    });

    const isTaskCompleted = (task: Task) => task?.status === TaskStatus.Completed;
    const isTaskDeadlineToday = (task: Task) => {
      if (!task?.deadline) {
        return false;
      }
      return DateTime.fromMillis(task?.deadline).hasSame(DateTime.local(), 'day');
    };

    return {
      showTasksPopper,
      isTaskCompleted,
      isTaskDeadlineToday,
      ParagraphSize,
      IconSize,
      t,
      milestoneProgress,
      milestoneDeadline,
    };
  },
});
</script>

<style lang="scss">
.milestone-description {
  margin-top: var(--spacing-100);

  .paragraph,
  .heading-5 {
    color: var(--gray-60);
  }

  .paragraph:first-of-type {
    margin-bottom: var(--spacing-25);
  }

  .heading-5 {
    margin-bottom: var(--spacing-50);
  }

  &.completed {
    .heading-5,
    .paragraph {
      color: var(--green-25);
    }
  }

  &.active {
    .heading-5 {
      color: var(--text-dark);
    }

    .paragraph:first-of-type {
      color: var(--text-gray);
    }

    .deadline {
      color: var(--blue-45);
    }
  }
}

.tasks-popper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-50);
  border-radius: var(--radius-medium);
  padding: var(--spacing-100);
  background: var(--white);
  box-shadow: var(--shadow-3);
  width: 15rem;
  z-index: 2;
  overflow: hidden;

  .task-title-container {
    display: flex;
    gap: var(--spacing-50);
    align-items: center;
    overflow: hidden;

    .counter {
      flex-shrink: 0;
      font-weight: 600;
      background: var(--gray-96);
      border-radius: 50%;
      width: 1.5rem;
      height: 1.5rem;
      line-height: 1.5rem;
      text-align: center;
      margin-right: var(--spacing-50);

      .icon-container {
        display: flex;
        height: 100%;
        justify-content: center;
        align-items: center;
      }

      &.today {
        color: var(--white);
        background: var(--blue-60);
      }
    }

    &.completed {
      h5 {
        color: var(--green-25);
      }

      .counter {
        background: var(--green-95);
        color: var(--green-25);

        .svg-icon {
          height: 0.8rem;
        }
      }
    }
  }
}
</style>
